import { Component, OnInit } from "@angular/core";
import { FormGroup, FormControl, Validators, AsyncValidatorFn, AbstractControl } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { debounceTime, distinctUntilChanged, map, switchMap, of, first, Observable } from "rxjs";
import { DataService } from "../../data.service";
import { GtagService } from "../../gtag/gtag.service";
import { ClientInfoModel, CargoFeaturesModel, ClientInfo_RegistrationModel } from "../../model";
import { AuthService } from "../auth.service";
import { AppConfigService } from "../../app-config.service";


@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css'],
    standalone: false
})
export class RegisterComponent implements OnInit {
    registerForm!: FormGroup;
    editMode: boolean = false;
    userData?: ClientInfoModel;
    cargoFeatures?: CargoFeaturesModel;
    isOfertaAccepted: boolean = false;
    ofertaText: String = "";
    cargoId!: number;

    constructor(
        private authService: AuthService,
        private dataService: DataService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private gtag: GtagService,
        private config: AppConfigService
    ) {
        this.cargoId = config.getConfig().cargoId;
        this.gtag.pageview2('User registration');

        this.authService.cargoFeatures().then((cargoFeatures: CargoFeaturesModel) => {
            this.cargoFeatures = cargoFeatures;

            if (this.cargoFeatures?.hasOferta) {
                this.dataService.getOferta(this.config.getConfig().cargoId).subscribe((data) => {
                    if (data && data != '') {
                        this.ofertaText = data;
                    } else {
                        this.isOfertaAccepted = true;
                    }
                })
            } else {
                this.isOfertaAccepted = true;
            }
        });


    }

    ngOnInit() {
        this.editMode = this.activatedRoute.snapshot.data['editProfileMode'];
        if (this.editMode) {
            this.isOfertaAccepted = true;
        }

        this.registerForm = new FormGroup({
            'first_name': new FormControl(null, [Validators.required, Validators.minLength(2)]),
            'last_name': new FormControl(null, [Validators.required, Validators.minLength(2)]),
            'login': new FormControl(null, [Validators.required, Validators.minLength(3)], [this.loginExistValidatorFn()]),
            'phone': new FormControl(null, [Validators.required, Validators.minLength(10)]),
            'email': new FormControl(null, [Validators.required, Validators.email, Validators.minLength(6)], [this.emailExistsValidatorFn()]),
            'passwd': new FormControl(null, [Validators.required, Validators.minLength(6)]),
            'passwd2': new FormControl(null, [Validators.required, Validators.minLength(6)], this.checkPassword2Validator.bind(this)),
            'client_code': new FormControl(null, [Validators.required, Validators.minLength(3)], [this.clientExistsValidatorFn()]),
            'client_city': new FormControl(null, [Validators.required, Validators.minLength(3)])
        });

        if (this.editMode) {
            // todo: need to unsubscribe later
            this.authService.userData().then(
                (data?: ClientInfoModel) => {
                    if (data) {
                        this.userData = data;
                        this.registerForm.patchValue(data);
                        this.registerForm.controls['login'].disable();
                        this.registerForm.controls['passwd'].disable();
                        this.registerForm.controls['passwd2'].disable();

                        this.registerForm.controls['email'].markAsTouched();
                        this.registerForm.controls['phone'].markAsTouched();
                        this.registerForm.controls['client_code'].markAsTouched();
                        this.registerForm.controls['client_city'].markAsTouched();
                    }
                }
            );
        }
    }

    submitForm() {
        if (this.registerForm.valid) {
            if (this.editMode) {
                const clientData: ClientInfo_RegistrationModel = { ...this.userData, ...this.registerForm.value };
                clientData.client_id = clientData.id;

                const subscr = this.authService.updateClient(clientData).subscribe({
                    next: (data) => {
                        alert('Данные пользователя обновлены!');
                        this.router.navigate(['/my-tracks']);
                    },
                    complete: () => {
                        subscr.unsubscribe();
                    },
                    error: (err) => {
                        subscr.unsubscribe();
                        alert(`Ошибка обновления данных пользователя. ${err}`);
                    }
                });
            } else {
                const newClientData =  {...this.registerForm.value, "cargo_id": this.config.getConfig().cargoId}
                const subscr = this.authService.registerClient(newClientData).subscribe({
                    next: (data) => {
                        alert('Пользователь зарегистрирован!');
                        if (this.cargoFeatures?.isNewUserAdminRequired) {
                            //this.location.back(); //todo: fix it
                        } else {
                            this.router.navigate(['/']);
                        }
                    },
                    complete: () => {
                        subscr.unsubscribe();
                    },
                    error: (err) => {
                        subscr.unsubscribe();
                        alert(`Ошибка регистрации пользователя. ${err}`);
                    }
                });
            }
        } else {
            for (let fieldName in this.registerForm.controls) {
                this.registerForm.controls[fieldName].markAsTouched();
            }
        }
    }

    loginExistValidatorFn(): AsyncValidatorFn {
        return control => control.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                map(() => this.editMode && this.userData?.login === control.value), // check if value changed
                switchMap(value => value ? of(null) : this.authService.isLoginExists(this.cargoId, control.value)), // request data from server
                map((exists: boolean) => (exists ? { loginAlreadyExist: true } : null)),
                first() // important to make observable finite
            );
    }

    emailExistsValidatorFn(): AsyncValidatorFn {
        return control => control.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                map(() => this.editMode && this.userData?.email === control.value), // check if value changed
                switchMap(value => value ? of(null) : this.authService.isEmailExists(this.cargoId, control.value)), // request data from server
                map((exists: boolean) => (exists ? { emailAlreadyExist: true } : null)),
                first() // important to make observable finite
            );
    }

    clientExistsValidatorFn(): AsyncValidatorFn {
        return control => control.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                map(() => this.editMode && this.userData?.client_code === control.value), // check if value changed
                switchMap(value => value ? of(null) : this.authService.isClientExists(this.cargoId, control.value)), // request data from server
                map((exists: boolean) => (exists ? { userAlreadyExist: true } : null)),
                first() // important to make observable finite
            );
    }

    checkPassword2Validator(control: AbstractControl): Promise<any> | Observable<any> {
        const promise = new Promise<any>((resolve, reject) => {
            const passwdField = this.registerForm.get('passwd');

            if (control.value && passwdField!.value && control.value === passwdField!.value) {
                resolve(null);
            } else {
                resolve({ passwd2Mismatch: true });
            }
        });

        return promise;
    }

    acceptOferta() {
        this.isOfertaAccepted = true;
    }

    checkIf(value: any) {
        debugger;  //open the devtools and go to the view...code execution will stop here!
        //..code to be checked... `value` can be inspected now along with all of the other component attributes
    }


}
